<template>
    <div>
        <div id="model-vision-platform-box" class="fullscreen overflow-y-auto py-20">

            <div v-if="layout.setup" class="vp-modal bg-white overflow-y-auto flex flex-col justify-center items-center">

            <!-- HEADER -->
            <div class="w-full relative px-10 pt-5">
                
                <div class="text-center mx-auto space-y-2">

                    <div class="text-2xl font-medium">
                        {{ $t('Vision Platform') }}
                    </div>
                    
                    <span
                        class="inline-block px-4 py-1 rounded-full text-xs font-semibold text-white"
                        :class="layout.trainingMode === 'labeling' ? 'bg-green-600' : 'rp-bg-blue-1'">    
                        {{ layout.trainingMode === 'labeling' ? $t('Labeling') : $t('Production') }}
                    </span>
                    
                    <div class="text-gray-700">
                        {{ $t('Use this dataset and annotation set to train a new model') }}
                    </div>

                </div>

                <button class="absolute top-1 right-1 m-4 hover:text-rp-blue-1 cursor-pointer" @click="$emit('closeModal')">
                    <XIcon class="w-8 h-8" />
                </button>

            </div>
            
            <!-- SETUP -->
            <div v-if="!layout.loading" class="px-20">
                
                <!-- TRAINING REQUIREMENTS -->
                <div class="mt-2" v-if="Object.keys(train.validation).length && !train.validation?.validated">

                    <div class="text-left w-full p-5 rounded border my-3">

                        <div class="mr-5 mb-3">{{$t('Now the dataset cannot be training, check requirements and warnings for train')}}.</div>
                        
                        <div class="grid grid-cols-12 mt-5 text-xs">
                            
                            <div class="col-span-7" v-if="train.validation.errors && train.validation.errors.length">
                                <div class="pr-3 text-red-400 text-sm mb-5"><AlertCircleIcon class="w-4 h-4" /> {{ train.validation.errors.length }} {{$t('Requirements')}}</div>
                                <div class="w-full text-truncate font-italic" v-for="(k) in train.validation.errors" :key="k">{{ k }}</div>
                            </div>

                            <div class="col-span-5" v-if="train.validation.warnings && train.validation.warnings.length">
                                <div class="text-sm mb-5"><AlertTriangleIcon class="w-4 h-4" /> {{ train.validation.warnings.length }} {{$t('Warnings')}}</div>
                                <div class="w-full text-truncate font-italic" v-for="(k) in train.validation.warnings" :key="k">{{ k }}</div> 
                            </div>

                        </div>
                        
                        <!-- <div class="mt-5 text-xs">
                            <span class="text-rp-blue-1 cursor-pointer" @click="manageTags()">Manage tags</span> | 
                            <span class="text-rp-blue-1 cursor-pointer" @click="linkToBrowser()">Labeling</span>
                        </div> -->

                    </div>

                </div>

                <!-- MODEL NAME -->
                <div class="mt-2">
                    <div class="text-left">
                        <div class="mb-3 ">{{ $t('Model name') }} *</div>
                        <input id="train-name" 
                            type="text" 
                            :maxlength="32" 
                            v-model="train.config.modelName" 
                            @input="removeHyphens()" 
                            :class="{'form-control py-3 px-4 border-transparent bg-gray-200 pr-10': true, 'border-red-500': duplicatedName}" 
                            required/>
                        <div v-if="duplicatedName" class="text-xs pt-2 text-red-500 text-center">
                            {{ $t('This model name is already in use. Please choose a different name.') }}
                        </div>
                        <div class="text-xs pt-2 text-center">{{ 
                            $t('Use up to 32 characters, which can include letters, numbers, and underscores') }}.
                        </div>
                    </div>
                </div>

                <!-- TAG SELECTION -->
                <div class="mt-5">
                    <div class="text-left">{{ $t('Select the tags you want to include in the training') }} *</div>

                    <div class="grid grid-cols-12 gap-1" v-if="train.tagMap">
                        
                        <!-- UNSELECTED TAGS -->
                        <div class="col-span-6 overflow-hidden">
                            <div class="bg-gray-300 h-full px-3">
                                <draggable class="list-group bg-gray-300 h-full pt-5 mt-5 rounded pb-3" :list="train.tagMap.labels" group="people" @change="refreshTagMap" v-if="Object.keys(props.dataset.tags).length">
                                    <template #item="{ element }"><div class="list-group-item bg-theme-2 text-white text-center mb-1 cursor-pointer p-2 mx-2 text-xs rounded truncate" :class="{ 'not-draggable': !enabled }">{{ element.name }}</div></template>
                                </draggable>
                                <div v-else class="pt-5 mb-5">{{ $t('Dataset does not contain tags') }} <div class="text-blue-500 text-xs text-center cursor-pointer" @click="manageTags()">{{ $t('create tags') }}</div></div>
                            </div>
                        </div>

                        <!-- SELECTED TAGS -->
                        <div class="col-span-6 mt-5 overflow-hidden" >
                            <div class="bg-green-100 h-full px-3">
                                <draggable class="list-group h-full pt-5 pb-10" :list="train.tagMap.selected" group="people" >
                                    <template #item="{ element }">
                                        <div class="list-group-item bg-green-500 text-white text-center mb-1 cursor-pointer p-2 mx-1 text-xs rounded truncate font-medium" :class="{ 'not-draggable': !enabled }">
                                            <div class="font-medium">{{ element.name }}</div>
                                            <input type="text" class="w-full py-2 px-3 border-transparent bg-white text-gray-900 mt-2" v-model="element.vertexLabel" :placeholder="'Label'" @change="refreshTagMap" />
                                        </div>
                                    </template>
                                </draggable>
                            </div>
                        </div>

                    </div>

                </div>

                <div class="mt-5" v-if="train.tagMap?.selected.length && train.config?.tagMap">

                    <!-- TRAINING TIME -->
                    <div class="mb-5 px-10">
                        <div class="text-base font-medium mb-5">{{ $t('Training time (minutes)') }}</div>
                        <div class="flex items-center justify-center mt-10">
                            <div class="flex-grow">
                                <Slider v-model="advancedSettings.time" :min="5" :max="180" :step="5" :tooltips="true"/>
                            </div>
                        </div>
                        <div class="flex items-center mt-4">
                            <label class="inline-flex items-center space-x-2">
                            <input
                                type="checkbox"
                                v-model="advancedSettings.earlyStopping"
                                class="form-checkbox h-5 w-5 text-primary"
                            />
                            <span class="text-sm">{{ $t('Enable early stopping') }}</span>
                            </label>
                        </div>
                    </div>

                    <!-- DATA DIVISION -->
                    <div class="mt-3 text-left">

                        <div class="pb-5 text-center">
                            <input v-model="train.config.method" class="form-check-input mr-1" type="radio" value="labeled" style="vertical-align: middle" /> 
                            <span class="font-medium" :class="train.config.method=='labeled' ? 'text-rp-blue-1' : ''">{{ $t('Current labeled') }}</span>
                            <input v-model="train.config.method" class="form-check-input ml-10 mr-1" type="radio" value="auto" style="vertical-align: middle" /> 
                            <span class="font-medium" :class="train.config.method=='auto' ? 'text-rp-blue-1' : ''">{{ $t('Auto assignment') }}</span> 
                        </div>

                        <div v-if="train.config.method == 'labeled'">
                            <div class="grid chat grid-cols-12 mb-10">
                                
                                <div class="col-span-5">
                                    <div v-if="train.division" class="p-5 pt-0">
                                        <div class="relative">
                                            <ReportDonutChart
                                                :height="190"
                                                :type="'doughnut'"
                                                :options="{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    cutout: '70%',
                                                    plugins: {
                                                        legend: { display: false }
                                                    }
                                                }"
                                                :data="{
                                                    labels: ['Train', 'Test', 'Validation', 'Predetermined'],
                                                    datasets: [{
                                                        data: [
                                                            train.division?.train || 0,
                                                            train.division?.test || 0,
                                                            train.division?.validation || 0,
                                                            train.division?.predetermined || 0
                                                        ],
                                                        backgroundColor: ['#333333', '#1d8102', '#09491b', '#b7b7b7']
                                                    }]
                                                }"
                                            />
                                            <div class="flex flex-col justify-center items-center absolute w-full h-full top-0 left-0">
                                                <div class="text-xl xxl:text-2xl font-medium">{{ parseInt(train.division?.total) }}</div>
                                                <div class="text-gray-600 dark:text-gray-600 mt-0.5">{{$t('Images')}}</div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div class="col-span-7">
                                    <div class="text-left text-xs mx-12">
                                        <div class="mb-2">{{ $t('Data division') }} *</div>
                                        {{ $t('Your datasets will be manually split into training, validation, and test sets with the following ratios') }}: 
                                    </div>
                                    <div class="mx-auto w-10/12 xxl:w-2/3 mt-5">
                                        <div class="flex items-center">
                                            <div class="w-3 h-3 bg-theme-26 rounded-full mr-3" style="background-color: #333333"></div>
                                            <span class="truncate">{{$t('Train')}}</span>
                                            <div class="h-px flex-1 border border-r border-dashed border-gray-400 mx-2 mt-3"></div>
                                            <span class="font-medium xl:ml-auto">
                                                {{ train.division?.train  }} ({{ ((train.division?.train*100)/train.division?.total).toFixed(0) }}%)
                                            </span>
                                        </div>
                                        <div class="flex items-center mt-2">
                                            <div class="w-3 h-3 bg-theme-35 rounded-full mr-3" style="background-color: #1d8102"></div>
                                            <span class="truncate">{{$t('Test')}}</span>
                                            <div class="h-px flex-1 border border-r border-dashed border-gray-400 mx-2 mt-3"></div>
                                            <span class="font-medium xl:ml-auto">
                                                {{ train.division?.test }} ({{ ((train.division?.test*100)/train.division?.total).toFixed(0) }}%)
                                            </span>
                                        </div>
                                        <div class="flex items-center mt-2">
                                            <div class="w-3 h-3 bg-theme-35 rounded-full mr-3" style="background-color: #09491b"></div>
                                            <span class="truncate">{{$t('Validation')}}</span>
                                            <div class="h-px flex-1 border border-r border-dashed border-gray-400 mx-2 mt-3"></div>
                                            <span class="font-medium xl:ml-auto">
                                                {{ train.division?.validation }} ({{ ((train.division?.validation*100) / train.division?.total).toFixed(0) }}%)
                                            </span>
                                        </div>
                                        <div class="flex items-center mt-2" v-if="train.division">
                                            <div class="w-3 h-3 bg-theme-35 rounded-full mr-3" style="background-color: #b7b7b7"></div>
                                            <span class="truncate">{{$t('Predetermined')}}</span>
                                            <div class="h-px flex-1 border border-r border-dashed border-gray-400 mx-2 mt-3"></div>
                                            <span class="font-medium xl:ml-auto">
                                                {{ train.division?.predetermined }} ({{ ((train.division?.predetermined*100) / train.division?.total).toFixed(0) }}%)
                                            </span>
                                        </div>
                                    </div>
                                    <!-- <div class="text-center text-xs mt-5">
                                        {{ $t('You can configure the set by image through') }} <span class="text-rp-blue-1 cursor-pointer" @click="linkToBrowser()">{{ $t('data labeling') }}</span>
                                    </div> -->
                                </div>

                            </div>
                        </div>

                        <div v-else-if="train.config.method == 'auto'">
                            <div class="grid chat grid-cols-12">

                                <div class="col-span-5 mb-10">

                                    <!-- Data division Diagram -->
                                    <div v-if="train.division" class="p-5 pt-0">
                                        <div class="relative">
                                            <ReportDonutChart 
                                                :height="190" 
                                                :options="{
                                                    responsive: true,
                                                    maintainAspectRatio: false,
                                                    cutout: '70%',
                                                    plugins: {
                                                        legend: { display: false }
                                                    }
                                                }"
                                                :data='{
                                                    labels: [ "Train", "Test", "validation" ],
                                                    datasets: [{
                                                        data: [ 
                                                            train.config.train, 
                                                            train.config.test, 
                                                            train.config.validation
                                                        ],
                                                        backgroundColor: [ "#333333", "#1d8102", "#09491b", "#b7b7b7" ]
                                                    }]
                                                }'
                                            />
                                            <div class="flex flex-col justify-center items-center absolute w-full h-full top-0 left-0">
                                                <div class="text-xl xxl:text-2xl font-medium">{{ parseInt(train.division?.total) }}</div>
                                                <div class="text-gray-600 dark:text-gray-600 mt-0.5">{{$t('Images')}}</div>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Data division -->
                                    <div class="mx-auto w-10/12 xxl:w-2/3">
                                        
                                        <div class="flex items-center">
                                            <div class="w-3 h-3 bg-theme-26 rounded-full mr-3" style="background-color: #333333"></div>
                                            <span class="truncate">{{$t('Train')}}</span>
                                            <div class="h-px flex-1 border border-r border-dashed border-gray-400 mx-2 mt-3"></div>
                                            <span class="font-medium xl:ml-auto">
                                                {{ ((train.config.train * train.division?.total) / 100).toFixed(0) }}
                                            </span>
                                        </div>

                                        <div class="flex items-center mt-2">
                                            <div class="w-3 h-3 bg-theme-35 rounded-full mr-3" style="background-color: #1d8102"></div>
                                            <span class="truncate">{{$t('Test')}}</span>
                                            <div class="h-px flex-1 border border-r border-dashed border-gray-400 mx-2 mt-3"></div>
                                            <span class="font-medium xl:ml-auto">
                                                {{ ((train.config.test * train.division?.total) / 100).toFixed(0) }}
                                            </span>
                                        </div>

                                        <div class="flex items-center mt-2">
                                            <div class="w-3 h-3 bg-theme-35 rounded-full mr-3" style="background-color: #09491b"></div>
                                            <span class="truncate">{{$t('Validation')}}</span>
                                            <div class="h-px flex-1 border border-r border-dashed border-gray-400 mx-2 mt-3"></div>
                                            <span class="font-medium xl:ml-auto">
                                                {{ ((train.config.validation * train.division?.total) / 100).toFixed(0) }} 
                                            </span>
                                        </div>

                                    </div>

                                </div> 

                                <div class="col-span-7">
                                    
                                    <div class="text-left text-xs mx-12">
                                        <div class="mb-2">{{ $t('Data division') }} *</div> 
                                        {{ $t('Your dataset will be automatically randomized and split into training, validation, and test sets with the following ratios') }}:
                                    </div>

                                    <div class="text-left text-xs mx-12 bg-red-200 text-gray-800 py-3 mt-5 pl-5" v-if="autoDivisionAlert">
                                        <AlertTriangleIcon class="w-4 h-4 mr-1" /> 
                                        {{$t('The division of data must add up to 100%')}}. 
                                    </div>

                                    <div class="grid chat grid-cols-12 mx-20 my-2 mt-8">
                                        
                                        <div class="col-span-12 text-left mb-4">
                                            <div class="mb-2">{{ $t('Train') }} 
                                                <span v-if="train.config.train" class="ml-auto float-right text-xs">{{ train.config.train }}%</span>
                                            </div>
                                            <Slider v-model="train.config.train" :min="50" :max="100" :step="5" :tooltips="false"/>
                                        </div>

                                        <div class="col-span-12 text-left mb-4">
                                            <div class="mb-2">{{ $t('Test') }}  
                                                <span v-if="train.config.test" class="ml-auto float-right text-xs">{{ train.config.test }}%</span>
                                            </div>
                                            <Slider v-model="train.config.test" :min="0" :max="50" :step="5" :tooltips="false"/>
                                        </div>

                                        <div class="col-span-12 text-left mb-4">
                                            <div class="mb-2">{{ $t('Validation') }} 
                                                <span v-if="train.config.validation" class="ml-auto float-right text-xs">{{ train.config.validation }}%</span>
                                            </div>
                                            <Slider v-model="train.config.validation" :min="0" :max="50" :step="5" :tooltips="false"/>
                                        </div>

                                    </div>

                                </div>

                            </div>
                        </div>

                    </div>
                    
                    <!-- ACTION BUTTONS -->
                    <div class="text-center my-5 flex justify-center space-x-4">
                        
                        <button v-if="layout.trainingMode !== 'labeling'"
                            type="button" 
                            class="btn bg-theme-15 w-44 zoom-in py-4"
                            @click="openAdvancedSettings()">
                            {{ $t('Advanced settings') }}
                        </button>

                        <button 
                            type="button" 
                            @click="handleTrainClick()" 
                            class="btn bg-theme-15 w-44 zoom-in py-4"
                            :class="{'opacity-50 cursor-not-allowed': !enableModelCreation}"
                            :disabled="!enableModelCreation">
                            {{ $t('Train') }}
                        </button>
                        
                    </div>

                </div>

            </div>

            <div v-else class="grid chat grid-cols-12">
                <div class="col-span-12 flex flex-col justify-center items-center pb-10">
                    <div class="flex items-center justify-center mt-10"><img :src="require(`@/assets/images/rosepetal/icon/logoLoadingGray.png`)" class="w-20" /></div>
                    <LoadingIcon icon="three-dots" class="w-8 h-8" />
                    <p class="text-sm">{{ $t('Loading data...') }}</p>
                </div>
            </div>

            <AdvancedSettings v-if="showAdvancedSettings" 
                :dataset="props.dataset.data"
                :advancedSettings="advancedSettings"
                @applySettings="advancedSettings = $event; showAdvancedSettings = false"
                @closeModal="showAdvancedSettings = false" />

        </div>

        <div v-else class="rp-modal flex flex-col items-center justify-center" style="width: 950px; height: fit-content;">

            <div v-if="!train.response.error">
                <div class="intro-y flex flex-col justify-center items-center  text-center my-20 mx-10">
                    <LoadingIcon icon="oval" class="w-20 h-20 mb-5" /><br />
                    {{$t('Synchronizing and creating the model')}}.<br /> 
                    {{$t('This process can take several minutes')}}.<br /> 
                    <span class="text-theme-5 text-base">{{$t("Don't close your browser")}}</span>
                </div>
            </div>

            <div v-else>
                <div class="text-left mt-5 text-gray-800 py-3 pl-5 mb-3">
                    <div>{{  $t('An error has occurred that prevents training from starting')  }}.</div>
                    <div class="py-3 text-red-600">{{ train.response.error }}</div>
                    <button type="button" class="btn bg-theme-15 text-gray-800 w-32 mr-l zoom-in py-2 mt-5" @click="$emit('closeModal')">{{ $t('Back') }}</button>
                </div>
            </div>

            </div>

        </div>

        <ValidationReminder v-if="showValidationReminder"
            :dataset="props.dataset.data"
            @closeModal="showValidationReminder = false"
            @validate="handleValidationChoice('validate')"
            @continueToTrain="handleValidationChoice('continue')" />

    </div>
</template>

<script setup>
import { onMounted, computed, watch, ref } from "vue";
import Slider 				from "@vueform/slider";
import draggable 			from "vuedraggable";

import ReportDonutChart		from "@/components/visuals/report-donut-chart/Main.vue";
import AdvancedSettings		from "@/modals/model/AdvancedSettings.vue";
import ValidationReminder	from "@/modals/dataset/ValidationReminder.vue";

import * as rosepetalModel	from "rosepetal-model";
import config 				from '@/etc/rosepetal.json';

const $emit = defineEmits(['modelCreated', 'switchToValidation']);

const $dataset	= rosepetalModel.default.dataset;
const $model	= rosepetalModel.default.model;
const $vision	= rosepetalModel.default.vision;

const autoDivisionAlert = computed(() => { 
	if (train.value.config) return train.value.config.test + train.value.config.train + train.value.config.validation != 100 ? true : false;
	return false;
});

const validModelName = computed(() => {
    return !layout.value.localModelsNames.includes(train.value.config.modelName) && train.value.config.modelName.length > 0;
})

const enableModelCreation = computed(() => {
	if (train.value.validation.validated && !autoDivisionAlert.value && validModelName.value) return true;
	else return false;
});

const duplicatedName = computed(() => {
	return layout.value.localModelsNames.includes(train.value.config.modelName);
});

const props = defineProps({
	dataset: {
		type: Object,
		required: true
	},
    trainingMode: {
        type: String,
        default: 'production'
    },
    dimensions: {
        type: Object,
        default: () => ({ width: 0, height: 0 })
    }
});

const showAdvancedSettings = ref(false);
const showValidationReminder = ref(false);

const layout = ref({
    setup:              true,
    loading:            false,
    dataset:            false,
    localModels:        [],
    localModelsNames:   [],
    trainingMode:       ''
});

const advancedSettings = ref({
	width:			0,
	height:			0,
	time: 			5,
	batchSize:		4,
	patience: 		0,
	earlyStopping:	false,
	taskType:		"",
	taskModel:		"",
	taskConfig: 	{}
});

const train = ref({
    response:   {},
    validation: {},
    division:   {},
    tagMap: {
        labels:     [],
        selected:   []
    },
    config: {
        tagMap: {
            total:  0,
            tags:   {},
            errors: [],
            resume: { division: { total: 0, test: 0, train: 0, validation: 0, predetermined: 0 } }
        },
        method: 				"auto",
        modelName: 				'model_undefined',
        train: 					80, 
        test: 					10,
        validation: 			10,
        modelTrainBudget: 		1000, 
        modelTrainBudgetMin: 	1000,
        modelTrainBudgetObjMin: 1000,
        modelTrainBudgetStep: 	1000,
        modelTrainBudgetMax: 	24000, 
        modelTrainBudgetObjMax:	30000,
    }
});

const refreshTagMap = async () => {
	if (train.value.tagMap.selected.length) {
		train.value.tagMap.selected.forEach(async (tag) => {
			const _tagName  = tag.vertexLabel || tag.id;

			if (!train.value.config.tagMap.tags[_tagName]) {
				train.value.config.tagMap.tags[_tagName] = { total: 0, tags: { count: 0 } };
			}

			train.value.config.tagMap.tags[_tagName]["tags"][tag.id] = { count: tag.counter };
			train.value.config.tagMap.tags[_tagName]["tags"]["count"]++;

			if (props.dataset.data.type == 'MULTICLASS' || props.dataset.data.type == 'ANOMALY') {
				train.value.config.tagMap.tags[_tagName]["tags"][tag.id]["count"] = tag.counter;

				train.value.config.tagMap.total += tag.counter;
				train.value.config.tagMap.tags[_tagName].total += tag.counter;

				Object.keys(tag.division).forEach((div) => {
					train.value.config.tagMap.resume.division[div] += tag.division[div];
				})
			}
		})

		if (props.dataset.data.type !== 'MULTICLASS' && props.dataset.data.type !== 'ANOMALY') { 
			const divisions = await $dataset.getDivisionStats(props.dataset.data, false, false, train.value.config.tagMap);

			train.value.config.tagMap.total = divisions.total;

			Object.keys(divisions).forEach((div) => {
				train.value.config.tagMap.resume.division[div] += divisions[div];
			});
		}
	} else {
        train.value.config.tagMap = {
            total:  0,
            tags:   {},
            errors: [],
            resume: { division: { total: 0, test: 0, train: 0, validation: 0, predetermined: 0 } }
        }
    }

	train.value.division = train.value.config.tagMap.resume.division;
    layout.value.dataset.data.division = train.value.division;


	let validationOpt = { 
        noDivision:     train.value.config.method == 'auto' ? true : false, 
        tagMap:         train.value.config.tagMap,
        labeledImages:  props.dataset.data.type == 'imageObjectDetection' ? true : false
    };

	train.value.validation = await $model.validateToTrain(layout.value.dataset, validationOpt);

    console.log("Train validation:", train.value);
}

const setupTraining = async () => {
	if (Object.keys(props.dataset.tags).length) {
		for (const tag of Object.values(props.dataset.tags)) {
			if (!tag.unclassified) {
				const tagObj = {
					name:     tag.name, 
					id:       tag.id, 
					counter:  props.dataset.tagStats.tags[tag.id] || 0, 
					color:    tag.color, 
					division: await $dataset.getDivisionStats(props.dataset.data, false, tag.id), 
					mask:     await $dataset.getMaskCounter(props.dataset.data.id, tag.id), 
				}

				train.value.tagMap.selected.push(tagObj);
			}
		}
	}

	refreshTagMap();

	train.value.config.modelName = props.dataset.data.name.toString().replace(/-/g, '_').toLowerCase();
    layout.value.loading = false;
}

const taskTypedefaultValue = computed (() => {
    if (props.dataset.data.type === 'imageObjectDetection') {
        return 'imageObjectDetection';
    } else if (props.dataset.data.type === 'MULTICLASS') {
        return 'classification';
    } else if (props.dataset.data.type === 'MULTILABEL') {
        return 'segmentation';
    } else if (props.dataset.data.type === 'ANOMALY') {
        return 'anomalyDetection';
    } else {
        return '';
    }
});

const modelTypedefaultValue = computed (() => {
    if (props.dataset.data.type === 'imageObjectDetection') {
        return 'yolov8';
    } else if (props.dataset.data.type === 'MULTICLASS') {
        return 'yolov8';
    } else if (props.dataset.data.type === 'MULTILABEL') {
        return 'yolov8';
    } else if (props.dataset.data.type === 'ANOMALY') {
        return 'padim';
    } else {
        return '';
    }
});

// Initialize all advanced settings defaults (replicates AdvancedSettings.vue logic)
const initializeAdvancedSettingsDefaults = () => {
	// Handle labeling mode (florence2)
	if (layout.value.trainingMode === 'labeling') {
		advancedSettings.value.taskModel = 'florence2';
		return; // Exit early for labeling mode
	}

	// Set taskType based on dataset type
	if (props.dataset.data.type === 'ANOMALY') {
		advancedSettings.value.taskType = 'anomalyDetection';
		advancedSettings.value.taskModel = 'padim';
		
		// Initialize complete taskConfig with all anomaly detection defaults
		advancedSettings.value.taskConfig = {
			// Shared anomaly options
			pre_trained: true,
			backbone: 'resnet18',
			
			// Patchcore-specific options  
			n_features: 100
		};
		
		// Set batch size to 1 for anomalies
		advancedSettings.value.batchSize = 1;
		
	} else if (props.dataset.data.type === 'MULTILABEL') {
		advancedSettings.value.taskType = 'segmentation';
		advancedSettings.value.taskModel = 'yolov8';
		advancedSettings.value.taskConfig = {
			size: 'Nano'
		};
	} else if (props.dataset.data.type === 'MULTICLASS') {
		advancedSettings.value.taskType = 'classification';
		advancedSettings.value.taskModel = 'yolov8';
		advancedSettings.value.taskConfig = {
			size: 'Nano'
		};
	} else {
		// imageObjectDetection and other types
		advancedSettings.value.taskType = 'imageObjectDetection';
		advancedSettings.value.taskModel = 'yolov8';
		advancedSettings.value.taskConfig = {
			size: 'Nano'
		};
	}
};

const openAdvancedSettings = () => {
    showAdvancedSettings.value = true;
};

const startTraining = async () => {
    layout.value.setup = false;

    // Initialize complete advanced settings as if the modal was opened
    initializeAdvancedSettingsDefaults();



	const opt = {
		dataset:	        props.dataset.data,
		modelName:	        train.value.config.modelName,
		tagMap:		        train.value.config.tagMap || false,
		dataDivision: {
			train:          train.value.config.train,
			test:		    train.value.config.test,
			validation:	    train.value.config.validation
		},
		advancedSettings:   advancedSettings.value
	};

    console.log("Training options:", opt);

	train.value.response = await $vision.manageRequest(opt, config.firebaseConfig.projectId);

    if (train.value.response.success) {
        $emit('modelCreated', opt.modelName);
	}
}

const linkToBrowser = () => { 
	cash(".modal").modal("hide");
	layout.value.modalImport = false; 
	router.push({ name: 'side-menu-datasets', params: { datasetID: props.dataset.id } });  
}

const handleTrainClick = () => {
	if (props.dataset.data.type === 'imageObjectDetection') {
		showValidationReminder.value = true;
	} else {
		startTraining();
	}
};

const handleValidationChoice = (choice) => {
	showValidationReminder.value = false;
	
	if (choice === 'validate') {
		$emit('switchToValidation');
	} else if (choice === 'continue') {
		startTraining();
	}
}

const removeHyphens = () => {
	train.value.config.modelName = train.value.config.modelName.replace(/-/g, '_').toLowerCase();
}

onMounted(async () => {
    console.log("VisionPlatform mounted", props);

    layout.value.loading = true;

    if (props.trainingMode) {
        layout.value.trainingMode = props.trainingMode;
    }

    if (props.dataset) {
        layout.value.dataset = props.dataset;

        layout.value.localModels = props.dataset.localModels;
	    layout.value.localModelsNames = props.dataset.localModels.map((model) => model.id);
    }

    if (props.dimensions) {
        advancedSettings.value.width = props.dimensions.width;
        advancedSettings.value.height = props.dimensions.height;
    }
    
    // Set default batch size for anomaly detection
    if (props.dataset.data.type === 'ANOMALY') {
        advancedSettings.value.batchSize = 1;
    }
    
    // Initialize advanced settings on component mount
    initializeAdvancedSettingsDefaults();
    
    await setupTraining();
});

watch(() => train.value.config.method, async () => {
	train.value.validation = {};
	let validateOpt = { noDivision: train.value.config.method == 'auto' ? true : false };
	if (train.value.config.tagMap) validateOpt.tagMap = train.value.config.tagMap;
	train.value.validation = await $model.validateToTrain(props.dataset, validateOpt);
});
</script>

<style>
.fullscreen { 
    position: fixed; 
    left: 0; 
    top: 0; 
    width: 100%; 
    height: 100%; 
    background-color: #f2f3f3; 
    z-index: 99999; 
    overflow: hidden;
    justify-items: center;
}

.vp-modal {
    width: 950px;
    min-width: 550px;
    background-color: #fff; 
    border-radius: 8px;
    box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 
                0 10px 10px -5px rgba(0, 0, 0, 0.04);
}
</style>